import React, {createRef, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import {
	OAuthProvider,
	OAuthProviderLogout,
	OAuthProviderLogoutInterface,
	OAuthProviderName
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import OAuthDialogFields from "../field/OAuthDialogFields";
import {InputText} from "primereact/inputtext";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {Menu} from "primereact/menu";
import Info, {InfoType} from "../../../shared/components/Info";

interface AddOAuthProviderDialogProperties extends AdminDialogProperties<OAuthProvider> {
	onSubmit: (parameter: OAuthProvider) => void;
	providerType: OAuthProviderName;
	logout: boolean;
}

export class AddOAuthProviderDialogDefinition extends AdminDialog<OAuthProvider,
	AddOAuthProviderDialogProperties, AdminDialogState<OAuthProvider>> {
	public renderContents(): ReactNode {
		let parameter: OAuthProvider | undefined = this.state.parameter;
		let errors: Map<OAuthDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let discoveryMenu: RefObject<Menu> = createRef<Menu>();
		let discoveryUrl: string = FunctionHelper.applyValue(
			parameter?.discoveryUrl, OAuthProvider.getDiscoveryUrlDefault()
		);

		let logoutMenu: RefObject<Menu> = createRef<Menu>();
		let logoutUrl: string = FunctionHelper.applyValue(
			parameter?.logout?.url, OAuthProviderLogout.getUrlDefault()
		);

		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-name">
						<Translate {...this.props} data="oauth.name" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getDisplayNameDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputText id="control-name"
								   className={errors.has(OAuthDialogFields.DISPLAY_NAME) ? "p-invalid" : ""}
								   tooltip={errors.has(OAuthDialogFields.DISPLAY_NAME) ? Translate.value(
									   errors.get(OAuthDialogFields.DISPLAY_NAME)!, {ns: "message"}
								   ) : ""}
								   value={FunctionHelper.applyValue(
									   parameter?.displayName, OAuthProvider.getDisplayNameDefault()
								   )}
								   onInput={(e) => this.onChange(
									   OAuthDialogFields.DISPLAY_NAME, e.currentTarget.value
								   )}/>
						{this.renderFieldError(errors.has(OAuthDialogFields.DISPLAY_NAME) ?
							Translate.value(
								errors.get(OAuthDialogFields.DISPLAY_NAME)!, {ns: "message"}
							) : ""
						)}
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-client-id">
						<Translate {...this.props} data="oauth.client_id" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getClientIdDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputText id="control-client-id"
								   className={errors.has(OAuthDialogFields.CLIENT_ID) ? "p-invalid" : ""}
								   tooltip={errors.has(OAuthDialogFields.CLIENT_ID) ? Translate.value(
									   errors.get(OAuthDialogFields.CLIENT_ID)!, {ns: "message"}
								   ) : ""}
								   value={FunctionHelper.applyValue(
									   parameter?.clientId, OAuthProvider.getClientIdDefault()
								   )}
								   onInput={(e) => this.onChange(
									   OAuthDialogFields.CLIENT_ID, e.currentTarget.value
								   )}/>
						{this.renderFieldError(errors.has(OAuthDialogFields.CLIENT_ID) ?
							Translate.value(
								errors.get(OAuthDialogFields.CLIENT_ID)!, {ns: "message"}
							) : ""
						)}
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-application-id">
						<Translate {...this.props} data="oauth.application_id" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getApplicationIdDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputText id="control-application-id"
								   className={errors.has(OAuthDialogFields.APPLICATION_ID) ? "p-invalid" : ""}
								   tooltip={errors.has(OAuthDialogFields.APPLICATION_ID) ? Translate.value(
									   errors.get(OAuthDialogFields.APPLICATION_ID)!, {ns: "message"}
								   ) : ""}
								   value={FunctionHelper.applyValue(
									   parameter?.applicationId, OAuthProvider.getApplicationIdDefault()
								   )}
								   onInput={(e) => this.onChange(
									   OAuthDialogFields.APPLICATION_ID, e.currentTarget.value
								   )}/>
						{this.renderFieldError(errors.has(OAuthDialogFields.APPLICATION_ID) ?
							Translate.value(
								errors.get(OAuthDialogFields.APPLICATION_ID)!, {ns: "message"}
							) : ""
						)}
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-tenant-id">
						<Translate {...this.props} data="oauth.tenant_id" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getTenantIdDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputText id="control-tenant-id"
								   value={FunctionHelper.applyValue(
									   parameter?.tenantId, OAuthProvider.getTenantIdDefault()
								   )}
								   onInput={(e) => this.onChange(
									   OAuthDialogFields.TENANT_ID, e.currentTarget.value
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-scope">
						<Translate {...this.props} data="oauth.scope" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getScopeDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputText id="control-scope"
								   value={FunctionHelper.applyValue(
									   parameter?.scope, OAuthProvider.getScopeDefault()
								   )}
								   onInput={(e) => this.onChange(
									   OAuthDialogFields.SCOPE, e.currentTarget.value
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-discovery-url">
						<Translate {...this.props} data="oauth.discovery_url" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getDiscoveryUrlDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<div className="p-inputgroup">
							<InputText id="control-discovery-url" value={discoveryUrl}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.DISCOVERY_URL, e.currentTarget.value
									   )}/>
							<Button icon="icon icon-angle-down" aria-controls={"control-discovery-url-menu"}
									onClick={(event) => discoveryMenu.current!.toggle(event)}
									className="p-inputgroup-addon" aria-haspopup={true}/>
							<Menu popup={true} ref={discoveryMenu} id="control-discovery-url-menu"
								  appendTo={document.getElementById("root")} model={[
								{
									label: Translate.value("oauth.vars.client_id", {ns: "dialog"}),
									command: (e) => this.onChange(
										OAuthDialogFields.DISCOVERY_URL, discoveryUrl + "{clientId}"
									)
								},
								{
									label: Translate.value("oauth.vars.app_id", {ns: "dialog"}),
									command: (e) => this.onChange(
										OAuthDialogFields.DISCOVERY_URL, discoveryUrl + "{appId}"
									)
								},
								{
									label: Translate.value("oauth.vars.tenant_id", {ns: "dialog"}),
									command: (e) => this.onChange(
										OAuthDialogFields.DISCOVERY_URL, discoveryUrl + "{tenantId}"
									)
								}
							]}/>
						</div>
					</div>
				</div>
				{this.props.logout &&
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-logout">
							<Translate {...this.props} data="oauth.logout" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProviderLogout.getUrlDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className="p-inputgroup">
								<InputText id="control-logout" value={logoutUrl}
										   onInput={(e) => this.onChange(
											   OAuthDialogFields.LOGOUT, e.currentTarget.value
										   )}/>
								<Button icon="icon icon-angle-down" aria-controls={"control-logout-menu"}
										onClick={(event) => logoutMenu.current!.toggle(event)}
										className="p-inputgroup-addon" aria-haspopup={true}/>
								<Menu popup={true} ref={logoutMenu} id="control-logout-menu"
									  appendTo={document.getElementById("root")} model={[
									{
										label: Translate.value("oauth.vars.redirect_url", {ns: "dialog"}),
										command: (e) => this.onChange(
											OAuthDialogFields.LOGOUT, logoutUrl + "{redirectUrl}"
										)
									},
									{
										label: Translate.value("oauth.vars.client_id", {ns: "dialog"}),
										command: (e) => this.onChange(
											OAuthDialogFields.LOGOUT, logoutUrl + "{clientId}"
										)
									},
									{
										label: Translate.value("oauth.vars.app_id", {ns: "dialog"}),
										command: (e) => this.onChange(
											OAuthDialogFields.LOGOUT, logoutUrl + "{appId}"
										)
									}
								]}/>
							</div>
						</div>
					</div>
				}
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-claim-client-id">
						<Translate {...this.props} data="oauth.claim_client_id" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getClaimClientIdDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputText id="control-claim-client-id"
								   className={errors.has(OAuthDialogFields.CLAIM_CLIENT_ID) ? "p-invalid" : ""}
								   tooltip={errors.has(OAuthDialogFields.CLAIM_CLIENT_ID) ? Translate.value(
									   errors.get(OAuthDialogFields.CLAIM_CLIENT_ID)!, {ns: "message"}
								   ) : ""}
								   value={FunctionHelper.applyValue(
									   parameter?.claimClientId, OAuthProvider.getClaimClientIdDefault()
								   )}
								   onInput={(e) => this.onChange(
									   OAuthDialogFields.CLAIM_CLIENT_ID, e.currentTarget.value
								   )}/>
						{this.renderFieldError(errors.has(OAuthDialogFields.CLAIM_CLIENT_ID) ?
							Translate.value(
								errors.get(OAuthDialogFields.CLAIM_CLIENT_ID)!, {ns: "message"}
							) : ""
						)}
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-claim-session-id">
						<Translate {...this.props} data="oauth.claim_session_id" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getClaimSessionIdDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputText id="control-claim-session-id"
								   className={errors.has(OAuthDialogFields.CLAIM_SESSION_ID) ? "p-invalid" : ""}
								   tooltip={errors.has(OAuthDialogFields.CLAIM_SESSION_ID) ? Translate.value(
									   errors.get(OAuthDialogFields.CLAIM_SESSION_ID)!, {ns: "message"}
								   ) : ""}
								   value={FunctionHelper.applyValue(
									   parameter?.claimSessionId, OAuthProvider.getClaimSessionIdDefault()
								   )}
								   onInput={(e) => this.onChange(
									   OAuthDialogFields.CLAIM_SESSION_ID, e.currentTarget.value
								   )}/>
						{this.renderFieldError(errors.has(OAuthDialogFields.CLAIM_SESSION_ID) ?
							Translate.value(
								errors.get(OAuthDialogFields.CLAIM_SESSION_ID)!, {ns: "message"}
							) : ""
						)}
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-claim-roles">
						<Translate {...this.props} data="oauth.claim_roles" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getClaimRolesDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputText id="control-claim-roles"
								   className={errors.has(OAuthDialogFields.CLAIM_ROLES) ? "p-invalid" : ""}
								   tooltip={errors.has(OAuthDialogFields.CLAIM_ROLES) ? Translate.value(
									   errors.get(OAuthDialogFields.CLAIM_ROLES)!, {ns: "message"}
								   ) : ""}
								   value={FunctionHelper.applyValue(
									   parameter?.claimRoles, OAuthProvider.getClaimRolesDefault()
								   )}
								   onInput={(e) => this.onChange(
									   OAuthDialogFields.CLAIM_ROLES, e.currentTarget.value
								   )}/>
						{this.renderFieldError(errors.has(OAuthDialogFields.CLAIM_ROLES) ?
							Translate.value(
								errors.get(OAuthDialogFields.CLAIM_ROLES)!, {ns: "message"}
							) : ""
						)}
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-role-admin">
						<Translate {...this.props} data="oauth.role_admin" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getRoleNameAdminDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputText id="control-role-admin"
								   className={errors.has(OAuthDialogFields.ROLE_ADMIN) ? "p-invalid" : ""}
								   tooltip={errors.has(OAuthDialogFields.ROLE_ADMIN) ? Translate.value(
									   errors.get(OAuthDialogFields.ROLE_ADMIN)!, {ns: "message"}
								   ) : ""}
								   value={FunctionHelper.applyValue(
									   parameter?.roleNameAdmin, OAuthProvider.getRoleNameAdminDefault()
								   )}
								   onInput={(e) => this.onChange(
									   OAuthDialogFields.ROLE_ADMIN, e.currentTarget.value
								   )}/>
						{this.renderFieldError(errors.has(OAuthDialogFields.ROLE_ADMIN) ?
							Translate.value(
								errors.get(OAuthDialogFields.ROLE_ADMIN)!, {ns: "message"}
							) : ""
						)}
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-role-user">
						<Translate {...this.props} data="oauth.role_user" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={OAuthProvider.getRoleNameUserDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputText id="control-role-user"
								   className={errors.has(OAuthDialogFields.ROLE_USER) ? "p-invalid" : ""}
								   tooltip={errors.has(OAuthDialogFields.ROLE_USER) ? Translate.value(
									   errors.get(OAuthDialogFields.ROLE_USER)!, {ns: "message"}
								   ) : ""}
								   value={FunctionHelper.applyValue(
									   parameter?.roleNameUser, OAuthProvider.getRoleNameUserDefault()
								   )}
								   onInput={(e) => this.onChange(
									   OAuthDialogFields.ROLE_USER, e.currentTarget.value
								   )}/>
						{this.renderFieldError(errors.has(OAuthDialogFields.ROLE_USER) ?
							Translate.value(
								errors.get(OAuthDialogFields.ROLE_USER)!, {ns: "message"}
							) : ""
						)}
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["oauth-provider-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					resizable={false} appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.oauth.provider"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-oauth"}/>
					<Translate {...this.props} data="oauth.add.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: OAuthDialogFields, value: any): void {
		let parameter: OAuthProvider | undefined = this.state.parameter as OAuthProvider;
		let errors: Map<OAuthDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case OAuthDialogFields.DISPLAY_NAME:
				if (this.props.providerType === OAuthProviderName.Generic) {
					if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
						errors.set(field, "errors.fields.set");
					} else if (errors.has(field)) {
						errors.delete(field);
					}
				}

				parameter.displayName = value;
				break;
			case OAuthDialogFields.CLIENT_ID:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.clientId = value;
				break;
			case OAuthDialogFields.APPLICATION_ID:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.applicationId = value;
				break;
			case OAuthDialogFields.TENANT_ID:
				parameter.tenantId = value;
				break;
			case OAuthDialogFields.SCOPE:
				parameter.scope = value;
				break;
			case OAuthDialogFields.DISCOVERY_URL:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.discoveryUrl = value;
				break;
			case OAuthDialogFields.LOGOUT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					parameter.logout = undefined;
				} else {
					parameter.logout = OAuthProviderLogout.fromJson({
						url: value
					} as OAuthProviderLogoutInterface);
				}
				break;
			case OAuthDialogFields.CLAIM_CLIENT_ID:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.claimClientId = value;
				break;
			case OAuthDialogFields.CLAIM_SESSION_ID:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.claimSessionId = value;
				break;
			case OAuthDialogFields.CLAIM_ROLES:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.claimRoles = value;
				break;
			case OAuthDialogFields.ROLE_ADMIN:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.roleNameAdmin = value;
				break;
			case OAuthDialogFields.ROLE_USER:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.roleNameUser = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.add")} disabled={!this.isValid()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		let parameter: OAuthProvider | undefined = this.state.parameter as OAuthProvider;

		if (!FunctionHelper.exists(parameter)) {
			return false;
		}

		if (this.props.providerType === OAuthProviderName.Generic && (
			!FunctionHelper.exists(parameter.displayName) || FunctionHelper.isEmpty(parameter.displayName)
		)) {
			return false
		}

		if (!FunctionHelper.exists(parameter.clientId) || FunctionHelper.isEmpty(parameter.clientId)) {
			return false
		}

		if (!FunctionHelper.exists(parameter.applicationId) || FunctionHelper.isEmpty(parameter.applicationId)) {
			return false
		}

		if (!FunctionHelper.exists(parameter.discoveryUrl) || FunctionHelper.isEmpty(parameter.discoveryUrl)) {
			return false
		}

		if (!FunctionHelper.exists(parameter.claimClientId) || FunctionHelper.isEmpty(parameter.claimClientId)) {
			return false
		}

		if (!FunctionHelper.exists(parameter.claimSessionId) || FunctionHelper.isEmpty(parameter.claimSessionId)) {
			return false
		}

		if (!FunctionHelper.exists(parameter.claimRoles) || FunctionHelper.isEmpty(parameter.claimRoles)) {
			return false
		}

		if (!FunctionHelper.exists(parameter.roleNameAdmin) || FunctionHelper.isEmpty(parameter.roleNameAdmin)) {
			return false
		}

		if (!FunctionHelper.exists(parameter.roleNameUser) || FunctionHelper.isEmpty(parameter.roleNameUser)) {
			return false
		}

		return super.isValid();
	}

	protected async submit(): Promise<void> {
		await super.submit();
		let parameter: OAuthProvider = this.state.parameter as OAuthProvider;
		this.props.onSubmit(parameter);
		this.hide();
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.OAUTH_PROVIDER;
	}
}

const AddOAuthProviderDialog = React.memo(AddOAuthProviderDialogDefinition,
	(
		prevProps: Readonly<AddOAuthProviderDialogProperties>,
		nextProps: Readonly<AddOAuthProviderDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default AddOAuthProviderDialog;